import React, {useEffect, useState, useMemo} from 'react'
import BreadCrumb from '../../../Common/BreadCrumb'
import {Card, Container, Row, Col, Table, Alert as BootstrapAlert} from 'react-bootstrap';
import logoDark from "../../../assets/images/bim-logo.png";
import logoLight from "../../../assets/images/bim-logo.png"
import {useParams} from 'react-router-dom';
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {getTransactionTypeLabel} from "../../../utils/labelUtils";
import {VideoModal} from "./components/VideoModal";
import {exportAlertsToExcel, printAlerts} from "../../../utils/exportUtils";
import {dashboardAPI} from "../../../services/api";
import {mockStores} from "../../../data/stores";

const AlertDetails = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    document.title = t('Alerts.alert_details')
    
    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    // For demo users, use Redux state
    const reduxAlerts = useSelector((state: any) => state.Transactions.alerts);
    const reduxTransactions = useSelector((state: any) => state.Transactions.transactions);
    
    const [alert, setAlert] = useState<Alert | null>(null);
    const [transaction, setTransaction] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [showVideoModal, setShowVideoModal] = useState(false);
    const showVideoAlert = () => setShowVideoModal(true);
    const hideVideoAlert = () => setShowVideoModal(false);

    useEffect(() => {
        if (!id) return;

        if (isDemoUser) {
            // Use Redux state for demo users
            const foundAlert = reduxAlerts.find((alert: Alert) => alert.id === id);
            if (foundAlert) {
                setAlert(foundAlert);
                if (foundAlert.receiptNumber) {
                    const foundTransaction = reduxTransactions.find(
                        (t: any) => t.receiptNumber === foundAlert.receiptNumber
                    );
                    if (foundTransaction) {
                        setTransaction(foundTransaction);
                    }
                }
            }
            return;
        }

        // Fetch alert from backend for real users
        const fetchAlert = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const alertData = await dashboardAPI.getAlertById(id);
                if (alertData) {
                    // Fetch store details if needed
                    if (alertData.store?.id) {
                        try {
                            const storesResponse: any = await dashboardAPI.getStores();
                            const stores = Array.isArray(storesResponse) ? storesResponse : (storesResponse?.data || []);
                            const store = stores.find((s: any) => s.id === alertData.store.id);
                            if (store) {
                                alertData.store = {
                                    id: store.id || '',
                                    name: store.name || '',
                                    city: store.city || '',
                                    province: store.province || '',
                                    phone: store.phone || '',
                                    address: store.address || '',
                                    active: store.active !== false,
                                    createdOn: store.createdOn || ''
                                };
                            }
                        } catch (error) {
                            console.error('Failed to fetch store:', error);
                        }
                    }
                    
                    setAlert(alertData);
                    
                    // Try to fetch transaction if we have transactionId
                    // Note: AlertDTO has transactionId but not receiptNumber
                    // We'll use transactionId as receiptNumber for now
                    if (alertData.receiptNumber) {
                        // For now, we don't have a getTransactionById endpoint
                        // So we'll leave transaction as null or try to fetch from transactions list
                        // This can be enhanced later
                    }
                } else {
                    setError('Alert not found');
                }
            } catch (error: any) {
                console.error('Failed to fetch alert:', error);
                setError(error?.response?.data?.message || error?.message || 'Failed to fetch alert');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAlert();
    }, [id, isDemoUser, reduxAlerts, reduxTransactions]);


    const printAlert = () => {
        let alerts = []
        alerts.push(alert);
        exportAlertsToExcel(alerts)
    }

    const downloadAlert = () => {
        let alerts = []
        alerts.push(alert);
        printAlerts(alerts)
    }


    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Alerts.alert_details'), path: "/alerts"},
                            {label: id as string, path: "/alerts"}
                        ]}
                    />
                    {error && (
                        <Row>
                            <Col>
                                <BootstrapAlert variant="danger" onClose={() => setError(null)} dismissible>
                                    {error}
                                </BootstrapAlert>
                            </Col>
                        </Row>
                    )}
                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col xxl={9}>
                                <Card>
                                    <Card.Body>
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    ) : alert ? (
                        <Row className="justify-content-center">
                            <Col xxl={9}>
                                <Card id="demo">
                                    <Card.Body>
                                    <Row className="p-4">
                                        <Col lg={9}>
                                            <h3 className="fw-bold mb-4">{t('Alerts.alert_id')}: {alert?.id}
                                                {
                                                    alert?.videoUrl && <button onClick={() => showVideoAlert()} className="btn"><i
                                                        className="las la-video fs-24"></i></button>
                                                }
                                                <button onClick={() => printAlert()} className="btn"><i
                                                    className="las la-print fs-24"></i></button>
                                                <button onClick={() => downloadAlert()} className="btn"><i
                                                    className="las la-download fs-24"></i></button>
                                            </h3>

                                            <Row className="g-4">
                                                <Col lg={6} className="col-6">
                                                    <p className="text-muted mb-1 text-uppercase fw-medium fs-14">
                                                        {t('TransactionList.receipt_no')}
                                                    </p>
                                                    <h5 className="fs-16 mb-0">
                                                        <span id="invoice-no">#{alert?.receiptNumber}</span>
                                                    </h5>
                                                </Col>

                                                <Col lg={6} className="col-6">
                                                    <p className="text-muted mb-1 text-uppercase fw-medium fs-14">{t('TransactionList.date')}</p>
                                                    <h5 className="fs-16 mb-0"><span
                                                        id="invoice-date">{alert?.date}</span></h5>
                                                </Col>

                                                <Col lg={6} className="col-6">
                                                    <p className="text-muted mb-1 text-uppercase fw-medium fs-14">{t('TransactionList.transaction_type')}
                                                    </p>
                                                    <span className="badge bg-success-subtle text-success fs-11"
                                                          id="payment-status">{getTransactionTypeLabel(t, transaction?.transaction_type)}</span>
                                                </Col>

                                                <Col lg={6} className="col-6">
                                                    <p className="text-muted mb-1 text-uppercase fw-medium fs-14">{t('TransactionList.total')}</p>
                                                    <h5 className="fs-16 mb-0"><span
                                                        id="total-amount">{transaction?.total}</span>
                                                    </h5>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col lg={3}>
                                            <div className="mt-sm-0 mt-3">
                                                <div className="mb-4">
                                                    <img src={logoDark} className="card-logo card-logo-dark"
                                                         alt="logo dark" height="17"/>
                                                    <img src={logoLight} className="card-logo card-logo-light"
                                                         alt="logo light" height="17"/>
                                                </div>
                                                <h5 className="text-muted mb-1"
                                                    id="address-details">{alert?.store?.name}</h5>
                                                <p className="text-muted mb-1"
                                                   id="address-details">{alert?.store?.address}</p>
                                                <h6 className="mb-0">{alert?.store?.phone}</h6>
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row className="p-4 border-top border-top-dashed">
                                        <Col lg={9}>
                                            <Row className="g-3">
                                                <div className="col-6">
                                                    <h6 className="text-muted text-uppercase fw-semibold mb-3">{t('Alerts.operator')}</h6>
                                                    <table className="table">
                                                        <tbody>
                                                        <tr>
                                                            <td>{t('Receipt.cashier')}</td>
                                                            <td className="fw-bold text-end">{transaction?.cashier}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{t('Receipt.checkout')} </td>
                                                            <td className="fw-bold text-end">{transaction?.checkout}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{t('CustomTypes.duration')}</td>
                                                            <td className="fw-bold text-end">{transaction?.duration}</td>
                                                        </tr>
                                                        </tbody>
                                                    </table>

                                                </div>

                                            </Row>
                                        </Col>
                                        {
                                            alert?.reviewedBy &&
                                            <Col lg={3}>
                                                <Row className="g-3">
                                                    <h6 className="text-muted text-uppercase fw-semibold mb-3">{t('Alerts.reviewer')}</h6>
                                                    <table className="table">
                                                        <tbody>
                                                        <tr>
                                                            <td className="fw-bold text-end">{alert?.reviewedBy}</td>
                                                        </tr>
                                                        <tr>
                                                            <td className="fw-bold text-end">{alert?.reviewDate}</td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </Row>
                                            </Col>
                                        }

                                    </Row>

                                    <Row>
                                        <Col lg={12}>
                                            <Card.Body className="card-body p-4">
                                                <div className="table-responsive">
                                                    <Table
                                                        className="table-borderless text-center table-nowrap align-middle mb-0">
                                                        <thead>
                                                        <tr className="table-active">
                                                            <th scope="col" style={{width: "50px"}}>#</th>
                                                            <th scope="col">{t('Receipt.item')}</th>
                                                            <th scope="col">{t('Receipt.unit_price')}</th>
                                                            <th scope="col">{t('Receipt.qty')}</th>
                                                            <th scope="col"
                                                                className="text-end">{t('Receipt.total')}</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody id="products-list">
                                                        {
                                                            transaction?.items?.map((item: any, index: number) => (

                                                                <tr>
                                                                    <th scope="row">{index}</th>
                                                                    <td className="text-start">
                                                                        <span className="fw-medium">{item.name}</span>
                                                                    </td>
                                                                    <td>{item.unitPrice.toFixed(2)}₺</td>
                                                                    <td>{item.qty}</td>
                                                                    <td className="text-end">{item.total.toFixed(2)} ₺</td>
                                                                </tr>

                                                            ))
                                                        }

                                                        </tbody>
                                                    </Table>
                                                </div>
                                                <div className="border-top border-top-dashed mt-2">
                                                    <Table
                                                        className="table-borderless table-nowrap align-middle mb-0 ms-auto"
                                                        style={{width: "250px"}}>
                                                        <tbody>
                                                        <tr>
                                                            <td>{t('Receipt.total_gross_amount')}</td>
                                                            <td className="text-end">{transaction?.total_gross_amount}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{t('Receipt.total_tax')}</td>
                                                            <td className="text-end">{transaction?.total_tax}</td>
                                                        </tr>
                                                        <tr className="border-top border-top-dashed fs-15">
                                                            <th scope="row">{t('Receipt.total')}</th>
                                                            <th className="text-end">{transaction?.total}</th>
                                                        </tr>
                                                        </tbody>
                                                    </Table>

                                                </div>
                                                <div className="mt-3">
                                                    <h6 className="text-muted text-uppercase fw-semibold mb-3">{t('Receipt.tender_type')}</h6>
                                                    {
                                                        transaction?.tender_type && <p className="text-muted mb-1">{t('Receipt.tender_type')}: <span
                                                            className="fw-medium"
                                                            id="payment-method"> {t(`TenderTypes.${transaction?.tender_type}`)}</span>
                                                        </p>
                                                    }

                                                    {
                                                        transaction?.credit_card && (
                                                            <div>
                                                                <p className="text-muted mb-1">{t('Receipt.credit_card')}
                                                                    <span
                                                                        className="fw-medium"
                                                                        id="card-number">{transaction?.credit_card}</span>
                                                                </p>
                                                            </div>
                                                        )
                                                    }
                                                </div>

                                            </Card.Body>
                                            {
                                                alert && <VideoModal
                                                    videoUrl={alert?.videoUrl}
                                                    show={showVideoModal}
                                                    onClose={hideVideoAlert}
                                                />
                                            }
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    ) : null}
                </Container>
            </div>
        </React.Fragment>
    )
}

export default AlertDetails
